import { useCallback, useEffect, useRef, useState } from 'react';
import { useFireLoginAnalytics } from '@/features/login/presentation/hooks/useFireLoginAnalytics';
import type { SizerService } from '../../application/services/SizerService';
import type { PincodeLookupResult, SizerState } from '../../domain/interfaces/SizerRepository';
import { resolveStateFromPincode } from '../../utils/resolveStateFromPincode.utils';
import {
  canSendRingSizerOtp,
  type RingSizerFieldErrors,
  validateRingSizerSendOtp,
  validateRingSizerSubmit,
  validateRingSizerVerifyOtp,
  validateRingSizerEmail,
  validateRingSizerMobile,
} from '../../utils/ringSizerForm.schema';

export interface RingSizerFormState {
  name: string;
  mobile: string;
  email: string;
  pincode: string;
  city: string;
  state: string;
  address: string;
  street: string;
  locality: string;
  landmark: string;
  otp: string;
  otpId: string;
}

const INITIAL_FORM: RingSizerFormState = {
  name: '',
  mobile: '',
  email: '',
  pincode: '',
  city: '',
  state: '',
  address: '',
  street: '',
  locality: '',
  landmark: '',
  otp: '',
  otpId: '',
};

export function useRingSizerForm(sizerService: SizerService, customizationId: number) {
  const { fireCompleteRegistration } = useFireLoginAnalytics();
  const [form, setForm] = useState<RingSizerFormState>(INITIAL_FORM);
  const [states, setStates] = useState<SizerState[]>([]);
  const [fieldErrors, setFieldErrors] = useState<RingSizerFieldErrors>({});
  const [otpSent, setOtpSent] = useState(false);
  const [showVerifyOtpField, setShowVerifyOtpField] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [otpMessageVariant, setOtpMessageVariant] = useState<'success' | 'error' | ''>('');
  const [isPincodeValidated, setIsPincodeValidated] = useState(false);
  const pendingPincodeLookupRef = useRef<PincodeLookupResult | null>(null);
  const verifiedOtpIdRef = useRef('');

  useEffect(() => {
    void sizerService.fetchStates().then(setStates).catch(() => setStates([]));
  }, [sizerService]);

  useEffect(() => {
    const pending = pendingPincodeLookupRef.current;
    if (!pending || states.length === 0) return;

    const resolvedState = resolveStateFromPincode(pending, states);
    setForm((prev) => ({ ...prev, state: resolvedState }));
    pendingPincodeLookupRef.current = null;
  }, [states]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timerId = window.setInterval(() => {
      setResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [resendCooldown]);

  const clearFieldError = useCallback((field: keyof RingSizerFieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const applyPincodeLookup = useCallback(
    (result: PincodeLookupResult) => {
      if (result.hasError || !result.isPincodeValid) {
        pendingPincodeLookupRef.current = null;
        setIsPincodeValidated(false);
        setFieldErrors((prev) => ({ ...prev, pincode: 'Invalid Pincode' }));
        setForm((prev) => ({ ...prev, city: '', state: '' }));
        return;
      }

      clearFieldError('pincode');
      setIsPincodeValidated(true);
      const resolvedState = resolveStateFromPincode(result, states);
      if (states.length > 0) {
        pendingPincodeLookupRef.current = null;
      } else {
        pendingPincodeLookupRef.current = result;
      }
      setForm((prev) => ({
        ...prev,
        city: result.city,
        state: resolvedState,
      }));
    },
    [clearFieldError, states]
  );

  const lookupPincode = useCallback(
    async (pincode: string) => {
      if (pincode.length !== 6) return false;
      try {
        const result = await sizerService.lookupPincode(pincode);
        applyPincodeLookup(result);
        return !result.hasError && result.isPincodeValid;
      } catch {
        pendingPincodeLookupRef.current = null;
        setIsPincodeValidated(false);
        return false;
      }
    },
    [applyPincodeLookup, sizerService]
  );

  const validateContactFieldOnBlur = useCallback(
    (field: 'email' | 'mobile') => {
      const error =
        field === 'email'
          ? validateRingSizerEmail(form.email)
          : validateRingSizerMobile(form.mobile);

      setFieldErrors((prev) => {
        const next = { ...prev };
        if (error) {
          next[field] = error;
        } else {
          delete next[field];
        }
        return next;
      });
    },
    [form.email, form.mobile],
  );

  const updateField = useCallback(
    (field: keyof RingSizerFormState, value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrorMessage('');

      if (field === 'pincode') {
        setIsPincodeValidated(false);
      }

      if (field === 'email' || field === 'mobile') {
        clearFieldError(field);
        return;
      }

      if (
        field in { name: 1, otp: 1, pincode: 1, city: 1, state: 1, address: 1, street: 1, locality: 1 }
      ) {
        clearFieldError(field as keyof RingSizerFieldErrors);
      }
    },
    [clearFieldError]
  );

  const sendOtp = useCallback(async () => {
    const errors = validateRingSizerSendOtp({
      email: form.email,
      mobile: form.mobile,
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...errors }));
      return;
    }

    setIsSendingOtp(true);
    setOtpMessage('');
    setOtpMessageVariant('');
    try {
      const result = await sizerService.sendOtp({
        mobile: form.mobile,
        email: form.email,
        otpId: verifiedOtpIdRef.current || form.otpId || undefined,
      });
      if (result.hasError) {
        setOtpMessage(result.errorMsg || 'Unable to send OTP');
        setOtpMessageVariant('error');
        return;
      }
      if (!result.otpId) {
        setOtpMessage('Unable to send OTP');
        setOtpMessageVariant('error');
        return;
      }
      verifiedOtpIdRef.current = result.otpId;
      setForm((prev) => ({ ...prev, otpId: result.otpId }));
      setOtpSent(true);
      setShowVerifyOtpField(true);
      setResendCooldown(30);
      setOtpMessage(otpSent ? 'OTP resent successfully!' : 'OTP sent successfully!');
      setOtpMessageVariant('success');
    } catch {
      setOtpMessage('Unable to send OTP');
      setOtpMessageVariant('error');
    } finally {
      setIsSendingOtp(false);
    }
  }, [form.email, form.mobile, form.otpId, otpSent, sizerService]);

  const verifyOtp = useCallback(async () => {
    const errors = validateRingSizerVerifyOtp({ otp: form.otp });
    if (Object.keys(errors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...errors }));
      return;
    }
    const activeOtpId = verifiedOtpIdRef.current || form.otpId;
    if (!activeOtpId) return;

    setIsVerifyingOtp(true);
    setOtpMessage('');
    setOtpMessageVariant('');
    try {
      const result = await sizerService.verifyOtp({
        otpId: activeOtpId,
        otpString: form.otp,
      });
      if (result.hasError) {
        setOtpMessage(result.errorMsg || 'Invalid OTP');
        setOtpMessageVariant('error');
        setOtpVerified(false);
        return;
      }
      if (result.otpId) {
        verifiedOtpIdRef.current = result.otpId;
      }
      setForm((prev) => ({
        ...prev,
        otp: '',
        otpId: result.otpId || prev.otpId,
      }));
      clearFieldError('otp');
      setOtpVerified(true);
      setShowVerifyOtpField(false);
      setResendCooldown(0);
      setOtpMessage('OTP verified');
      setOtpMessageVariant('success');
    } catch {
      setOtpMessage('Unable to verify OTP');
      setOtpMessageVariant('error');
    } finally {
      setIsVerifyingOtp(false);
    }
  }, [clearFieldError, form.otp, form.otpId, sizerService]);

  const submit = useCallback(async () => {
    if (!otpVerified) {
      setErrorMessage('Please verify OTP before submitting');
      return;
    }

    const errors = validateRingSizerSubmit({
      name: form.name,
      email: form.email,
      mobile: form.mobile,
      pincode: form.pincode,
      city: form.city,
      state: form.state,
      address: form.address,
      street: form.street,
      locality: form.locality,
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (!isPincodeValidated) {
      const pincodeValid = await lookupPincode(form.pincode);
      if (!pincodeValid) {
        setFieldErrors((prev) => ({ ...prev, pincode: 'Invalid Pincode' }));
        return;
      }
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setFieldErrors({});
    try {
      const result = await sizerService.bookProductSizer({
        email: form.email,
        mobile: form.mobile,
        name: form.name,
        address: form.address,
        street: form.street,
        locality: form.locality,
        landmark: form.landmark,
        city: form.city,
        state: form.state,
        postcode: form.pincode,
        customizationId,
        otpId: verifiedOtpIdRef.current || form.otpId,
      });
      if (result.hasError) {
        setErrorMessage(result.message || 'Unable to book sizer');
        return;
      }
      if (result.isNewCustomerV2) {
        fireCompleteRegistration({
          registrationMethod: 'phone_otp',
          email: form.email.trim(),
          phone: form.mobile.trim().replace(/\s/g, ''),
          userCode: result.userCode,
          firstName: form.name.trim().split(/\s+/)[0] ?? '',
          postcode: form.pincode.trim(),
          state: form.state.trim(),
          city: form.city.trim(),
        });
      }
      setIsSubmitted(true);
    } catch {
      setErrorMessage('Unable to book sizer');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    customizationId,
    fireCompleteRegistration,
    form,
    isPincodeValidated,
    lookupPincode,
    otpVerified,
    sizerService,
  ]);

  const reset = useCallback(() => {
    setForm(INITIAL_FORM);
    setFieldErrors({});
    setOtpSent(false);
    setShowVerifyOtpField(false);
    setOtpVerified(false);
    setResendCooldown(0);
    setIsSubmitted(false);
    setErrorMessage('');
    setOtpMessage('');
    setOtpMessageVariant('');
    setIsPincodeValidated(false);
    pendingPincodeLookupRef.current = null;
    verifiedOtpIdRef.current = '';
  }, []);

  const canSendOtp = canSendRingSizerOtp({ email: form.email, mobile: form.mobile });

  return {
    form,
    states,
    fieldErrors,
    otpSent,
    showVerifyOtpField,
    otpVerified,
    resendCooldown,
    isSubmitting,
    isSendingOtp,
    isVerifyingOtp,
    isSubmitted,
    errorMessage,
    otpMessage,
    otpMessageVariant,
    canSendOtp,
    updateField,
    validateContactFieldOnBlur,
    lookupPincode,
    sendOtp,
    verifyOtp,
    submit,
    reset,
  };
}
